import { useEffect, useRef } from 'react';
import { Icon } from '@y365/design-system';
import type { ValidationError } from '@y365/shared';

interface Props {
  errors: ValidationError[];
  /** Bumped on each failed submit, so a repeat submit re-announces. */
  submitCount: number;
}

/**
 * Summary of everything wrong, at the top of the form, focused on appearance.
 *
 * Inline messages alone are not enough. Someone using a screen reader, or
 * anyone on a long form, submits and then has no idea where the problem is —
 * they have to hunt. This moves focus here, announces the count, and every
 * entry jumps to its field.
 */
export function ErrorSummary({ errors, submitCount }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errors.length > 0) ref.current?.focus();
  }, [errors.length, submitCount]);

  if (errors.length === 0) return null;

  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      aria-labelledby="error-summary-title"
      style={{
        background: 'var(--bg-surface)',
        border: '1.5px solid var(--y-danger)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        marginBottom: 'var(--sp-6)',
        outline: 'none',
      }}
    >
      <h2
        id="error-summary-title"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          margin: 0,
          marginBottom: 'var(--sp-3)',
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--fs-h4)',
          fontWeight: 'var(--fw-bold)',
          color: 'var(--y-danger)',
        }}
      >
        <Icon name="alert-circle" size={20} />
        {errors.length === 1
          ? 'There is one thing to fix'
          : `There are ${errors.length} things to fix`}
      </h2>

      <ul style={{ margin: 0, paddingLeft: 'var(--sp-5)' }}>
        {errors.map((error, i) => (
          <li key={`${error.path}-${i}`} style={{ marginBottom: 'var(--sp-2)' }}>
            <a
              href={`#field-${error.path}`}
              data-y365-focusable
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(`field-${error.path}`);
                el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                el?.focus();
              }}
              style={{
                color: 'var(--y-danger)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-body)',
                textDecoration: 'underline',
              }}
            >
              {error.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
